import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, InjectionToken, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter, fromEvent } from 'rxjs';

const ACTIVITY_SIGNAL_INTERVAL_MS = 5 * 60 * 1000;

export const SESSION_ACTIVITY_NOW = new InjectionToken<() => number>('SESSION_ACTIVITY_NOW', {
  factory: () => Date.now,
});
export const SESSION_ACTIVITY_IS_REAL_EVENT = new InjectionToken<(event: Event) => boolean>(
  'SESSION_ACTIVITY_IS_REAL_EVENT',
  { factory: () => (event) => event.isTrusted },
);

@Injectable({ providedIn: 'root' })
export class SessionActivityService {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _document = inject(DOCUMENT);
  private readonly _isRealEvent = inject(SESSION_ACTIVITY_IS_REAL_EVENT);
  private readonly _now = inject(SESSION_ACTIVITY_NOW);
  private readonly _router = inject(Router);

  private _hasPendingActivity = false;
  private _lastSignaledAt = Number.NEGATIVE_INFINITY;

  constructor() {
    fromEvent(this._document, 'click', { capture: true })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event) => this._recordDomActivity(event));
    fromEvent(this._document, 'input', { capture: true })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event) => this._recordDomActivity(event));
    this._router.events
      .pipe(
        filter(
          (event): event is NavigationStart =>
            event instanceof NavigationStart && event.navigationTrigger === 'popstate',
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => this._recordActivity());
  }

  claimActivitySignal(): boolean {
    if (!this._hasPendingActivity || this._now() - this._lastSignaledAt < ACTIVITY_SIGNAL_INTERVAL_MS) {
      return false;
    }

    this._hasPendingActivity = false;
    this._lastSignaledAt = this._now();
    return true;
  }

  reset(): void {
    this._hasPendingActivity = false;
    this._lastSignaledAt = Number.NEGATIVE_INFINITY;
  }

  private _recordActivity(): void {
    this._hasPendingActivity = true;
  }

  private _recordDomActivity(event: Event): void {
    if (this._isRealEvent(event)) {
      this._recordActivity();
    }
  }
}
